import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getBasicDetails, getBasicDetailsKey } from '../../api-client/account'
import { useAuth } from '../../contexts/auth'
import ShieldMark from '../svg/shieldmark'
import Checkmark from '../svg/checkmark'
import Modal from '../modal'
import { Button } from '../button'
import Avatar from '../avatar'
import DealerAvatarSvg from '../svg/avatar-dealer'
import AvatarSvg from '../svg/avatar'
import { getYear, parseISO } from '../../utils/date-service'

export default function UserInfoModal({ info, onClose, onAskQuestion }) {
  const { profileImageUrl, displayName, joinedIn, isDealer } = info
  const [error, setError] = useState()
  const [{ isAuthenticated }] = useAuth()
  const { data: { verified = false } = {} } = useQuery({
    queryKey: getBasicDetailsKey,
    queryFn: getBasicDetails,
    enabled: isAuthenticated,
  })

  const handleAskQuestionClick = () => {
    if (!isAuthenticated) setError('Please log in to be able to send messages')
    else if (!verified) setError('Please complete the ID Verification to be able to send messages')
    else onAskQuestion()
  }

  const joinedInYear = getYear(parseISO(joinedIn, { withTime: true }))

  return (
    <Modal onClose={onClose}>
      <div className='userInfoWrapper'>
        <Avatar
          alt={`${displayName}'s avatar`}
          src={profileImageUrl}
          width={180}
          height={180}
          className='avatar'
          fallbackComponent={isDealer ? DealerAvatarSvg : AvatarSvg}
        />

        <div className='name'>{displayName}</div>
        <div className='joinedIn'>Joined in {joinedInYear}</div>

        <ul className='accountStatuses'>
          <li>
            <span>
              <ShieldMark width='16' height='20' fill='var(--primary-color)' />
            </span>
            <span>Verified User</span>
          </li>
        </ul>

        <div className='confirmationsContainer'>
          <p className='title'>{displayName} Confirmed</p>
          <ul className='confirmations'>
            <li>
              <span>
                <Checkmark width='20' height='20' fill='var(--success-color)' />
              </span>
              <span>Email Address</span>
            </li>
          </ul>
        </div>

        <Button className='askQuestion' onClick={handleAskQuestionClick} disabled={error}>
          Ask {displayName} a Question
        </Button>

        {error && <p className='error'>{error}</p>}
      </div>

      <style jsx>{`
        .userInfoWrapper {
          max-width: 100vw;
          width: 43.6rem;
          padding: 2.8rem 2rem 5rem;
          background: #fff;
          border-radius: 1rem;
          font-weight: 700;

          :global(.askQuestion) {
            display: block;
            margin: 3rem auto 0;
          }

          :global(img),
          :global(.avatar) {
            display: block;
            width: 18rem;
            height: 18rem;
            margin: 0 auto;
            object-fit: cover;
            border-radius: 50%;
          }
        }

        .name {
          margin: 1rem 0 0;
          text-align: center;
          font-size: 4.4rem;
        }

        .joinedIn {
          color: var(--secondary-text-color);
          text-align: center;
          font-size: 1.6rem;
        }

        .accountStatuses {
          padding: 0 0 2.5rem;
          border-bottom: 0.1rem solid var(--separator-line-color);
        }

        .confirmationsContainer {
          margin: 4rem 0 0;

          .title {
            margin: 0 0 0.8rem;
            color: var(--secondary-text-color);
            font-size: 1.6rem;
          }
        }

        .error {
          position: absolute;
          color: var(--alert-color);
          padding: 0.8rem 0 0 0;
          line-height: 1.8rem;
          font-size: 1.6rem;
          font-weight: 500;
        }

        li {
          padding: 1rem 0;

          span {
            vertical-align: middle;

            &:nth-child(1) {
              width: 2rem;
              margin: 0 0.5rem 0 0;
            }

            &:nth-child(2) {
              font-size: 1.8rem;
            }
          }
        }
      `}</style>
    </Modal>
  )
}
